import SentryService from '@services/SentryService';

import { IndexedDBStorageBackend } from './IndexedDBStorageBackend';
import { OPFSStorageBackend } from './OPFSStorageBackend';
import { selectStorageBackend } from './selectStorageBackend';
import type {
  IStorageBackend,
  StorageBackendType,
  StorageEventHandler,
  StorageEventMap,
  StorageState,
  StorageStatus,
} from './types';

type EventListeners = {
  [K in keyof StorageEventMap]: Set<StorageEventHandler<K>>;
};

/**
 * Facade for chunk storage — detects the best backend, initializes it,
 * and delegates all IStorageBackend methods.
 *
 * Provides `subscribe()`/`getSnapshot()` for React `useSyncExternalStore`.
 *
 * Usage:
 *   const storage = new StorageService();
 *   await storage.initialize();
 *   await storage.writeChunk(sessionId, 0, arrayBuffer);
 */
export class StorageService {
  private backend: IStorageBackend | null = null;
  private backendOverride: IStorageBackend | null;
  private initPromise: Promise<void> | null = null;
  private fallbackPromise: Promise<boolean> | null = null;

  private state: StorageState = {
    status: 'uninitialized',
    backendType: null,
    isPersisted: false,
    error: null,
  };

  private subscribers = new Set<() => void>();

  private listeners: EventListeners = {
    statusChanged: new Set(),
    backendSelected: new Set(),
    error: new Set(),
    quotaWarning: new Set(),
    persistenceChanged: new Set(),
    backendSwitched: new Set(),
  };

  /**
   * @param backendOverride — inject an IStorageBackend for testing
   *   (e.g., InMemoryStorageBackend). Skips detection + OPFS/IndexedDB init.
   */
  constructor(backendOverride?: IStorageBackend) {
    this.backendOverride = backendOverride ?? null;
  }

  // ── Lifecycle ──

  async initialize(forceBackend?: StorageBackendType): Promise<void> {
    if (this.state.status === 'ready') return;

    // If another call is already initializing, wait for it instead of starting a parallel init
    if (this.initPromise) {
      await this.initPromise;
      return;
    }

    this.initPromise = this.doInitialize(forceBackend);
    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  private async doInitialize(forceBackend?: StorageBackendType): Promise<void> {
    this.setStatus('initializing');

    try {
      // Dev-only: check for injected storage init failure (persisted in sessionStorage
      // by ChaosController so it survives the page refresh needed to re-trigger init)
      if (import.meta.env.DEV) {
        const chaosType = sessionStorage.getItem('__chaos_storageInitFailure__');
        if (chaosType && chaosType !== 'none') {
          const errors: Record<string, DOMException> = {
            quota: new DOMException('Chaos: quota exceeded', 'QuotaExceededError'),
            security: new DOMException('Chaos: storage blocked', 'SecurityError'),
            corrupted: new DOMException('Chaos: database corrupted', 'UnknownError'),
          };
          throw errors[chaosType] ?? new Error('Chaos: unknown storage failure');
        }
      }

      // Use override if provided (test mode — InMemoryStorageBackend mimics IndexedDB semantics)
      if (this.backendOverride) {
        this.backend = this.backendOverride;
        this.setState({ ...this.state, backendType: 'indexeddb' });
        this.setStatus('ready');
        return;
      }

      // Detect backend
      const type = forceBackend ?? (await selectStorageBackend());
      this.emit('backendSelected', { type });

      SentryService.setTag('storage_backend', type);
      SentryService.addBreadcrumb({
        category: 'storage.service',
        message: `Selected backend: ${type}`,
        level: 'info',
      });

      // Create and initialize backend
      if (type === 'opfs') {
        const opfs = new OPFSStorageBackend();
        try {
          await opfs.initialize();
          this.backend = opfs;
          this.setState({ ...this.state, backendType: 'opfs' });
          this.setStatus('ready');
        } catch (opfsError) {
          // OPFS failed at runtime — fall back to IndexedDB
          SentryService.addBreadcrumb({
            category: 'storage.service',
            message: 'OPFS init failed, falling back to IndexedDB',
            level: 'warning',
            data: { error: String(opfsError) },
          });
          opfs.dispose();

          const idb = new IndexedDBStorageBackend();
          await idb.initialize();
          this.backend = idb;
          this.setState({ ...this.state, backendType: 'indexeddb' });
          this.setStatus('ready');
        }
      } else {
        const idb = new IndexedDBStorageBackend();
        await idb.initialize();
        this.backend = idb;
        this.setState({ ...this.state, backendType: 'indexeddb' });
        this.setStatus('ready');
      }

      // Request persistent storage (best-effort)
      await this.requestPersistence();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.setState({ ...this.state, status: 'error', error: message });
      this.emit('error', { message, cause: error });
      SentryService.captureException(
        error instanceof Error ? error : new Error(`StorageService init failed: ${message}`),
      );
      throw error;
    }
  }

  // ── IStorageBackend delegation ──

  async writeChunk(sessionId: string, chunkIndex: number, data: ArrayBuffer): Promise<void> {
    try {
      await this.ensureReady().writeChunk(sessionId, chunkIndex, data);

      // Verify write persisted (lightweight — checks existence without loading data)
      const exists = await this.ensureReady().hasChunk(sessionId, chunkIndex);
      if (!exists) {
        throw new Error(
          `Write verification failed: chunk ${chunkIndex} not found after write`,
        );
      }

      SentryService.addBreadcrumb({
        category: 'storage.service',
        message: `Chunk ${chunkIndex} written and verified (${data.byteLength} bytes)`,
        level: 'info',
      });
    } catch (error) {
      // Attempt runtime fallback (OPFS → IndexedDB)
      const switched = await this.fallbackToIndexedDB(error, 'writeChunk');
      if (switched) {
        await this.ensureReady().writeChunk(sessionId, chunkIndex, data);
        const exists = await this.ensureReady().hasChunk(sessionId, chunkIndex);
        if (!exists) {
          throw new Error('Write verification failed after backend fallback');
        }
        return;
      }

      if (
        error instanceof DOMException &&
        error.name === 'QuotaExceededError' &&
        navigator.storage?.estimate
      ) {
        const estimate = await navigator.storage.estimate();
        this.emit('quotaWarning', {
          usage: estimate.usage ?? 0,
          quota: estimate.quota ?? 0,
        });
      }
      throw error;
    }
  }

  async readChunk(sessionId: string, chunkIndex: number): Promise<ArrayBuffer | null> {
    try {
      return await this.ensureReady().readChunk(sessionId, chunkIndex);
    } catch (error) {
      const switched = await this.fallbackToIndexedDB(error, 'readChunk');
      if (switched) {
        return await this.ensureReady().readChunk(sessionId, chunkIndex);
      }
      throw error;
    }
  }

  async hasChunk(sessionId: string, chunkIndex: number): Promise<boolean> {
    return this.ensureReady().hasChunk(sessionId, chunkIndex);
  }

  async deleteChunk(sessionId: string, chunkIndex: number): Promise<void> {
    return this.ensureReady().deleteChunk(sessionId, chunkIndex);
  }

  async listChunks(sessionId: string): Promise<number[]> {
    return this.ensureReady().listChunks(sessionId);
  }

  async deleteSession(sessionId: string): Promise<void> {
    return this.ensureReady().deleteSession(sessionId);
  }

  async listSessions(): Promise<string[]> {
    return this.ensureReady().listSessions();
  }

  /** Delete all sessions and their chunks. Used for emergency storage cleanup. */
  async clearAllSessions(): Promise<void> {
    const backend = this.ensureReady();
    const sessions = await backend.listSessions();
    await Promise.all(sessions.map(sid => backend.deleteSession(sid)));
  }

  /** Get storage usage estimate. Returns null if not available. */
  async getStorageEstimate(): Promise<{ usageBytes: number; quotaBytes: number } | null> {
    if (!navigator.storage?.estimate) return null;
    const estimate = await navigator.storage.estimate();
    if (estimate.quota == null || estimate.usage == null) return null;
    return { usageBytes: estimate.usage, quotaBytes: estimate.quota };
  }

  // ── React integration (useSyncExternalStore) ──

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  getSnapshot(): StorageState {
    return this.state;
  }

  // ── Events ──

  on<K extends keyof StorageEventMap>(event: K, handler: StorageEventHandler<K>): void {
    (this.listeners[event] as Set<StorageEventHandler<K>>).add(handler);
  }

  off<K extends keyof StorageEventMap>(event: K, handler: StorageEventHandler<K>): void {
    (this.listeners[event] as Set<StorageEventHandler<K>>).delete(handler);
  }

  // ── Cleanup ──

  dispose(): void {
    if (this.backend) {
      if ('dispose' in this.backend) {
        (this.backend as OPFSStorageBackend).dispose();
      } else if ('close' in this.backend) {
        (this.backend as IndexedDBStorageBackend).close();
      }
    }

    this.backend = null;
    this.initPromise = null;
    this.fallbackPromise = null;

    for (const set of Object.values(this.listeners)) {
      set.clear();
    }
    this.subscribers.clear();

    this.setState({
      status: 'uninitialized',
      backendType: null,
      isPersisted: false,
      error: null,
    });

    SentryService.addBreadcrumb({
      category: 'storage.service',
      message: 'StorageService disposed',
      level: 'info',
    });
  }

  // ── Private ──

  /**
   * Runtime fallback: if OPFS writes/reads fail after successful init,
   * switch to IndexedDB and let the caller retry.
   * Returns true if the backend was switched (caller should retry the operation).
   *
   * Uses a deduplication guard so concurrent failing writes all wait for
   * the same migration rather than racing. During the migration window,
   * new writes fail on OPFS → enter catch → call this method → wait on
   * fallbackPromise → get `true` back → retry on IndexedDB.
   */
  private async fallbackToIndexedDB(error: unknown, operation: string): Promise<boolean> {
    // Already switched — caller should retry immediately
    if (this.state.backendType !== 'opfs') return true;
    // Don't fall back on quota errors — IndexedDB would hit the same limit
    if (error instanceof DOMException && error.name === 'QuotaExceededError') return false;

    // If a fallback is already in progress, wait for it
    if (this.fallbackPromise) {
      return this.fallbackPromise;
    }

    this.fallbackPromise = this.doFallbackToIndexedDB(error, operation);
    try {
      return await this.fallbackPromise;
    } finally {
      this.fallbackPromise = null;
    }
  }

  private async doFallbackToIndexedDB(error: unknown, operation: string): Promise<boolean> {
    const oldBackend = this.backend;

    try {
      SentryService.captureException(
        new Error(
          `OPFS runtime fallback → IndexedDB (${operation}: ${error instanceof Error ? error.message : String(error)})`,
        ),
      );

      // Initialize IndexedDB BEFORE disposing OPFS so we can attempt migration
      const idb = new IndexedDBStorageBackend();
      await idb.initialize();

      // Best-effort migration: copy any accessible OPFS chunks to IndexedDB.
      // If OPFS reads are also broken this will fail fast and we move on —
      // whatever we salvage is better than nothing.
      if (oldBackend) {
        try {
          const sessions = await oldBackend.listSessions();
          for (const sid of sessions) {
            const chunks = await oldBackend.listChunks(sid);
            let copied = 0;
            const failedIndices: number[] = [];

            for (const idx of chunks) {
              try {
                const data = await oldBackend.readChunk(sid, idx);
                if (data) {
                  await idb.writeChunk(sid, idx, data);
                }
                copied++;
              } catch (chunkError) {
                failedIndices.push(idx);
                SentryService.addBreadcrumb({
                  category: 'storage.service',
                  message: `Migration: chunk ${idx} read failed from OPFS for session ${sid}: ${chunkError instanceof Error ? chunkError.message : String(chunkError)}`,
                  level: 'warning',
                });
              }
            }

            const hasFailures = failedIndices.length > 0;
            SentryService.addBreadcrumb({
              category: 'storage.service',
              message: hasFailures
                ? `Migrated session ${sid}: ${copied}/${chunks.length} chunks copied, ${failedIndices.length} failed (indices: ${failedIndices.join(', ')})`
                : `Migrated session ${sid}: ${copied}/${chunks.length} chunks copied`,
              level: hasFailures ? 'warning' : 'info',
            });
          }
        } catch {
          SentryService.addBreadcrumb({
            category: 'storage.service',
            message: 'OPFS → IndexedDB migration failed (OPFS reads broken), proceeding without migration',
            level: 'warning',
          });
        }
      }

      // Now dispose OPFS — migration is complete (or failed), safe to terminate worker
      if (oldBackend && 'dispose' in oldBackend) {
        (oldBackend as OPFSStorageBackend).dispose();
      }

      this.backend = idb;
      this.setState({ ...this.state, backendType: 'indexeddb' });
      SentryService.setTag('storage_backend', 'indexeddb');
      this.emit('backendSwitched', { from: 'opfs', to: 'indexeddb' });

      SentryService.addBreadcrumb({
        category: 'storage.service',
        message: `Runtime fallback complete: OPFS → IndexedDB (triggered by ${operation})`,
        level: 'warning',
      });

      return true;
    } catch (fallbackError) {
      SentryService.addBreadcrumb({
        category: 'storage.service',
        message: `Runtime fallback to IndexedDB also failed: ${String(fallbackError)}`,
        level: 'error',
      });
      return false;
    }
  }

  private ensureReady(): IStorageBackend {
    if (!this.backend || this.state.status !== 'ready') {
      throw new Error('StorageService not ready — call initialize() first');
    }
    return this.backend;
  }

  private setStatus(status: StorageStatus): void {
    const prev = this.state.status;
    if (prev === status) return;
    this.setState({ ...this.state, status });
    this.emit('statusChanged', { status, prev });
  }

  private setState(next: StorageState): void {
    this.state = next;
    this.notifySubscribers();
  }

  private notifySubscribers(): void {
    for (const cb of this.subscribers) {
      cb();
    }
  }

  private emit<K extends keyof StorageEventMap>(event: K, data: StorageEventMap[K]): void {
    for (const handler of this.listeners[event]) {
      try {
        handler(data);
      } catch (error) {
        SentryService.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }

  async requestPersistence(): Promise<boolean> {
    try {
      if (!navigator.storage?.persist) return false;

      const persisted = await navigator.storage.persist();
      this.setState({ ...this.state, isPersisted: persisted });
      this.emit('persistenceChanged', { persisted });

      SentryService.setTag('storage_persisted', String(persisted));
      SentryService.addBreadcrumb({
        category: 'storage.service',
        message: persisted
          ? 'Persistent storage granted'
          : 'Persistent storage DENIED — data at risk of eviction',
        level: persisted ? 'info' : 'warning',
      });

      return persisted;
    } catch (error) {
      SentryService.addBreadcrumb({
        category: 'storage.service',
        message: `Persistence request failed: ${String(error)}`,
        level: 'warning',
      });
      return false;
    }
  }
}
